import * as Msal from '@azure/msal-browser';
import { AuthConfig, LoginRequest } from './AuthConfig';
import Axios from 'axios';
import { User } from '../types/ViewModel/User';
import { useContext, useRef, useEffect, useState } from 'react';
import { useAuthStore } from '../context/AuthContext';
import getContext from '../context/AppSettingsContext';
import { useToast } from 'src/app/useToast';

// Create a singleton MSAL instance to prevent multiple instances with the same client ID
let msalInstance: Msal.PublicClientApplication | null = null;
let msalInitializationPromise: Promise<void> | null = null;
let isGloballyInitialized = false;

const pendingTokenPromises = new Map<string, Promise<Msal.AuthenticationResult | null>>();
let pendingRedirectPromise: Promise<Msal.AuthenticationResult | null> | null = null;

function getTokenCacheKey(authObject: { account?: Msal.AccountInfo; msalAccount?: Msal.AccountInfo; scopes?: string[]; authority?: string }): string {
    const accountId = (authObject.account ?? authObject.msalAccount)?.homeAccountId ?? '';
    const scopes = [...(authObject.scopes ?? [])].sort().join(',');
    const authority = authObject.authority ?? '';
    return `${accountId}|${scopes}|${authority}`;
}

const getMsalInstance = (): Msal.PublicClientApplication => {
    if (!msalInstance) {
        msalInstance = new Msal.PublicClientApplication(AuthConfig);
    }
    return msalInstance;
};

const initializeMsalSingleton = async (): Promise<void> => {
    if (isGloballyInitialized) {
        return Promise.resolve();
    }

    if (msalInitializationPromise) {
        return msalInitializationPromise;
    }

    msalInitializationPromise = (async () => {
        try {
            const instance = getMsalInstance();
            await instance.initialize();
            isGloballyInitialized = true;
        } catch (error) {
            console.error('MSAL initialization failed:', error);
            msalInitializationPromise = null; // Reset for retry
            throw error;
        }
    })();

    return msalInitializationPromise;
};

function handleRedirectOnce(msalObj: Msal.PublicClientApplication): Promise<Msal.AuthenticationResult | null> {
    if (pendingRedirectPromise) return pendingRedirectPromise;

    pendingRedirectPromise = msalObj
        .handleRedirectPromise()
        .then((result) => result)
        .finally(() => {
            pendingRedirectPromise = null;
        });

    return pendingRedirectPromise;
}

export default () => {
    const { setLoggedInUser, loggedInUser, setUserIsUpdated, isAuthenticating, setIsAuthenticating, setIsVerifyingToken, setToken } = useAuthStore();
    const { toastError } = useToast();
    const myMsalObj = getMsalInstance();
    const [isInitialized, setIsInitialized] = useState(false);
    const appSettings = useContext(getContext());
    // Use a ref to track authentication state that can be updated immediately
    const isAuthenticatingRef = useRef(isAuthenticating); // Initialize MSAL instance
    useEffect(() => {
        const handleInitialization = async () => {
            try {
                await initializeMsalSingleton();
                setIsInitialized(true);
            } catch (error) {
                console.error('MSAL initialization failed in hook:', error);
                // Retry initialization after a delay
                setTimeout(() => {
                    console.log('Retrying MSAL initialization...');
                    handleInitialization();
                }, 2000);
            }
        };

        if (isGloballyInitialized) {
            setIsInitialized(true);
        } else {
            handleInitialization();
        }
    }, []);

    // Update the ref when the state changes
    const updateAuthenticatingState = (newValue: boolean) => {
        isAuthenticatingRef.current = newValue;
        setIsAuthenticating(newValue);
    };
    const acquireToken = async (authObject: any): Promise<Msal.AuthenticationResult | null> => {
        if (!isInitialized) {
            return null;
        }

        const key = getTokenCacheKey(authObject);
        const existing = pendingTokenPromises.get(key);
        if (existing) return existing;

        const promise = (async () => {
            try {
                await handleRedirectOnce(myMsalObj);
                return await myMsalObj.acquireTokenSilent(authObject);
            } catch (error) {
                console.error('refreshAccessToken error interceptAxiosRequest:', error);

                if (error instanceof Msal.InteractionRequiredAuthError) {
                    // Prevent infinite loops by checking if we're already in a redirect flow
                    if (isAuthenticatingRef.current) {
                        console.warn('Already in authentication flow, preventing duplicate redirect');
                        return null;
                    }

                    // Check if we're in an iframe context to avoid unsafe navigation errors
                    if (window.self !== window.top) {
                        console.warn('Cannot perform redirect from iframe context');
                        throw new Error('Authentication required but cannot redirect from iframe context');
                    }

                    try {
                        updateAuthenticatingState(true);
                        // Use popup instead of redirect to avoid iframe issues
                        const result = await myMsalObj.acquireTokenPopup(authObject);
                        updateAuthenticatingState(false);
                        return result;
                    } catch (errorInner) {
                        console.error('acquireTokenPopup failed:', errorInner);
                        updateAuthenticatingState(false);

                        // If popup fails, fall back to redirect only if not in iframe
                        if (window.self === window.top) {
                            try {
                                await myMsalObj.acquireTokenRedirect(authObject);
                                return null; // Redirect will handle the response
                            } catch (redirectError) {
                                console.error('acquireTokenRedirect failed:', redirectError);
                                throw redirectError;
                            }
                        }
                        throw errorInner;
                    }
                }

                // Handle other types of errors
                console.error('Token acquisition failed:', error);
                throw error;
            } finally {
                pendingTokenPromises.delete(key);
            }
        })();

        pendingTokenPromises.set(key, promise);
        return promise;
    };

    const interceptSWRequest = async () => {
        if (!isInitialized) {
            return null;
        }
        await handleRedirectOnce(myMsalObj);
        let msalAccount: Msal.AccountInfo | null = null;
        const account = myMsalObj.getActiveAccount();

        if (account && AuthConfig.auth) {
            msalAccount = account;
        } else {
            const accounts = myMsalObj.getAllAccounts();
            if (accounts && accounts.length > 0 && AuthConfig.auth) {
                msalAccount = accounts[0];
            }
        }
        const authResult = await acquireToken({
            msalAccount,
            scopes: LoginRequest.scopes,
            authority: AuthConfig.auth.authority,
        });
        if (authResult && authResult.accessToken && authResult.accessToken.length > 0) {
            setToken(authResult.accessToken);
            return authResult.accessToken;
        } else {
            loginAzAd();
            return;
        }
    };
    const interceptAxiosRequest = (account: Msal.AccountInfo) => {
        Axios.interceptors.request.use(async (config) => {
            const signalRConnectionJson = localStorage.getItem('signalRConnection');
            const signalRConnection = signalRConnectionJson && JSON.parse(signalRConnectionJson);

            if (signalRConnection?.url && config?.url && sameParam(signalRConnection?.url, config?.url ?? '', 'hub')) {
                config.headers.Authorization = 'Bearer ' + signalRConnection?.accessToken;
            } else {
                // Add retry tracking to prevent infinite loops
                const retryCount = (config as any)._msalRetryCount || 0;
                if (retryCount >= 2) {
                    console.error('Max MSAL token retry attempts reached');
                    throw new Error('Authentication failed after multiple attempts');
                }

                try {
                    const authResult = await acquireToken({
                        account,
                        scopes: LoginRequest.scopes,
                        authority: AuthConfig.auth.authority,
                    });
                    if (authResult && authResult.accessToken && authResult.accessToken.length > 0) {
                        config.headers.Authorization = 'Bearer ' + authResult.accessToken;
                        setToken(authResult.accessToken);
                    } else {
                        console.error('Failed to acquire access token - no token returned');
                        throw new Error('Failed to acquire access token.');
                    }
                } catch (error) {
                    console.error('Failed to acquire token for request:', error);

                    // Check if this is an iframe navigation error
                    if (error instanceof Error && error.message.includes('iframe')) {
                        console.warn('Iframe navigation error detected, redirecting to login');
                        setTimeout(() => {
                            window.top?.location.assign('/login');
                        }, 100);
                        throw new Error('Authentication required - redirecting to login');
                    }

                    // Mark this request for retry
                    (config as any)._msalRetryCount = retryCount + 1;

                    if (retryCount === 0) {
                        // On first failure, try again
                        throw error;
                    } else {
                        // On subsequent failures, show error and redirect
                        toastError('Authentication error. Please refresh the page', 8000);
                        throw new Error('Failed to inject token into authorization header.');
                    }
                }
            }

            config.headers.set('Transaction-Id', crypto.randomUUID());

            if (!config?.headers?.Authorization || (config.headers.Authorization as string).length < 10) {
                console.error('no authorization header');
                throw new Error('Failed to inject token into authorization header.');
            }
            config.params = { ...config.params, releaseId: appSettings?.releaseId };
            return config;
        });
    };

    const sameParam =  (str1: string, str2: string, param: string): boolean => {
        const params1 = new URLSearchParams(new URL(str1).search);
        const params2 = new URLSearchParams(new URL(str2).search);
        return params1.has(param) && params2.has(param) && params1.get(param) === params2.get(param);
    };

    const checkTokenAndSetUser = async (authenticationResult: Msal.AuthenticationResult) => {
        try {
            interceptAxiosRequest(authenticationResult.account);
            setUser(authenticationResult);
        } catch (error) {
            console.error('checkTokenAndSetUser error:', error);
        }
    };
    const loginAzAd = () => {
        // Check the ref value which is always up-to-date
        if (isAuthenticatingRef.current || !isInitialized) return;

        // Check if we're in an iframe context
        if (window.self !== window.top) {
            console.warn('Cannot perform login redirect from iframe context, redirecting parent window');
            window.top?.location.assign('/login');
            return;
        }

        updateAuthenticatingState(true);
        myMsalObj
            .loginRedirect()
            .then(() => {
                updateAuthenticatingState(false);
            })
            .catch((error) => {
                console.error('Login redirect failed:', error);
                updateAuthenticatingState(false);

                // If redirect fails, try popup as fallback
                if (error.message?.includes('iframe') || error.message?.includes('navigation')) {
                    console.log('Attempting login popup as fallback...');
                    myMsalObj
                        .loginPopup()
                        .then(() => {
                            updateAuthenticatingState(false);
                        })
                        .catch((popupError) => {
                            console.error('Login popup also failed:', popupError);
                            updateAuthenticatingState(false);
                            toastError('Login failed. Please try refreshing the page.', 5000);
                        });
                }
            });
    };
    const logoutAzAd = async (): Promise<void> => {
        if (!isInitialized) {
            throw new Error('[MSAL] Cannot log out before MSAL initialization completes.');
        }

        const account = loggedInUser
            ? loggedInUser.userName
                ? myMsalObj.getAccountByUsername(loggedInUser.userName)
                : null
            : myMsalObj.getActiveAccount() ?? myMsalObj.getAllAccounts()[0];

        await myMsalObj.logoutRedirect({
            account,
            postLogoutRedirectUri: '/login',
        });
    };
    const checkAuthStatus = async () => {
        if (!isInitialized) {
            return null;
        }
        let authResult: Msal.AuthenticationResult | void;
        let msalAccount: Msal.AccountInfo | null = null;

        try {
            await handleRedirectOnce(myMsalObj);
            setIsVerifyingToken(true);

            const account = myMsalObj.getActiveAccount();

            if (account && AuthConfig.auth) {
                msalAccount = account;
            } else {
                const accounts = myMsalObj.getAllAccounts();
                if (accounts && accounts.length > 0 && AuthConfig.auth) {
                    msalAccount = accounts[0];
                }
            }

            if (msalAccount && AuthConfig.auth) {
                authResult = await acquireToken({
                    account: msalAccount,
                    scopes: LoginRequest.scopes,
                    authority: AuthConfig.auth.authority,
                });

                if (authResult && authResult.accessToken && authResult.accessToken.length > 0) {
                    setToken(authResult.accessToken);
                    await checkTokenAndSetUser(authResult);
                } else {
                    const signedInAccounts = myMsalObj.getAllAccounts();
                    if (signedInAccounts && signedInAccounts.length > 0) {
                        await logoutAzAd();
                    }
                }
            } else {
                // No account found - do NOT force an interactive redirect here.
                // checkAuthStatus() is a silent status check invoked on every route change;
                // login must only ever be initiated explicitly by the user (see loginAzAd()
                // calls from the Login page buttons).
                setLoggedInUser(null);
            }
        } catch (error) {
            console.error('checkAuthStatus failed:', error);
            setLoggedInUser(null);
        } finally {
            setIsVerifyingToken(false);
        }

        return authResult;
    };

    const setUser = (authenticationResult: Msal.AuthenticationResult) => {
        if (!isInitialized) {
            return;
        }
        if (authenticationResult && authenticationResult.account) {
            const myAccount = authenticationResult.account.username ? myMsalObj.getAccountByUsername(authenticationResult.account.username) : null;
            myMsalObj.setActiveAccount(myAccount);
            if (myAccount) {
                const user = new User([], false, myAccount.name, myAccount.username);

                setLoggedInUser(user);
                setUserIsUpdated(true);
                updateAuthenticatingState(false);
            } else {
                setLoggedInUser(null);
            }
        } else {
            setLoggedInUser(null);
            setUserIsUpdated(true);
        }
    };

    const initHandleRedirect = () => {
        if (!isInitialized) {
            return;
        }
        handleRedirectOnce(myMsalObj)
            .then((result) => {
                if (result) {
                    checkTokenAndSetUser(result);
                }
            })
            .catch(() => {
                console.error('msal handleRedirectPromise');
            });
    };

    return {
        loginAzAd,
        logoutAzAd,
        checkAuthStatus,
        initHandleRedirect,
        interceptSWRequest,
        isInitialized,
    };
};
